import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { MapPin, CalendarDays, ArrowRight, Trophy, Check, Plus } from "lucide-react";
import { TripShareButton } from "@/components/trips/TripShareButton";

const fmtDates = (a, b) => [a, b].filter(Boolean).join(" – ");
const has = (ev, tags) => (ev.age_divisions || []).some((d) => tags.includes(d));
const isUnder = (ev) => has(ev, ["Underclass"]);
const FILTERS = [
  { id: "all", label: "All Trips", test: () => true },
  { id: "underclass", label: "Underclass", test: isUnder },
  { id: "upperclass", label: "Upperclass", test: (ev) => !isUnder(ev) && has(ev, ["17U", "18U"]) },
  { id: "14-16", label: "14U–16U", test: (ev) => !isUnder(ev) && !has(ev, ["17U", "18U", "19U"]) && has(ev, ["14U", "15U", "16U"]) },
];
const isClosed = (ev) => ev.interest_open === false || ev.application_status === "closed";

export default function Trips() {
  const [events, setEvents] = useState(null);
  const [filter, setFilter] = useState("all");
  const [picked, setPicked] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/interest-events").then(({ data }) => {
      const list = Array.isArray(data) ? data : (data.items || []);
      const visible = list.filter((e) => e.public_visible !== false && e.active !== false);
      visible.sort((a, b) => (isClosed(a) - isClosed(b)) || (a.start_date || "").localeCompare(b.start_date || ""));
      setEvents(visible);
    }).catch(() => setEvents([]));
  }, []);

  const toggle = (slug) => setPicked((p) => (p.includes(slug) ? p.filter((s) => s !== slug) : [...p, slug]));
  const shown = (events || []).filter(FILTERS.find((f) => f.id === filter).test);

  return (
    <PublicLayout>
      <section className="bg-[#0A192F] text-white border-b-4 border-[#E63946]">
        <div className="max-w-5xl mx-auto px-6 py-16 text-center">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#E63946] bg-white/5 px-3 py-1.5 rounded-sm">
            <Trophy className="w-3.5 h-3.5" /> CBR Nation
          </div>
          <h1 data-testid="trips-title" className="font-display text-4xl sm:text-6xl uppercase tracking-tighter mt-4">CBR Nation Trips</h1>
          <p className="text-white/70 text-sm sm:text-base mt-4 max-w-2xl mx-auto leading-relaxed">
            Tap a trip to register your interest — or tick <b>Add</b> on several trips and fill out one form for all of them.
          </p>
        </div>
      </section>

      <section className="bg-[#F1F3F6] py-14 min-h-[50vh] pb-32">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-wrap gap-2 mb-8" data-testid="trips-filters">
            {FILTERS.map((f) => (
              <button key={f.id} data-testid={`trips-filter-${f.id}`} onClick={() => setFilter(f.id)}
                className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-full border transition-colors ${filter === f.id ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-slate-600 border-slate-300 hover:border-[#0A192F]"}`}>
                {f.label}
              </button>
            ))}
          </div>
          {events === null ? (
            <div className="text-center text-xs uppercase tracking-wider text-slate-400 py-20">Loading events…</div>
          ) : shown.length === 0 ? (
            <div className="text-center text-slate-500 py-20" data-testid="trips-empty">No trips match right now. Check back soon!</div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2" data-testid="trips-grid">
              {shown.map((ev) => <TripCard key={ev.id || ev.slug} ev={ev} selected={picked.includes(ev.slug)} onToggle={() => toggle(ev.slug)} />)}
            </div>
          )}
        </div>
      </section>

      {picked.length > 0 && (
        <div data-testid="trips-selection-bar" className="fixed bottom-0 inset-x-0 z-40 bg-[#0A192F] border-t-4 border-[#E63946] text-white">
          <div className="max-w-5xl mx-auto px-6 pt-4 pb-16 sm:pb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm"><span className="font-display text-xl" data-testid="trips-selected-count">{picked.length}</span> trip{picked.length > 1 ? "s" : ""} selected
              <button data-testid="trips-clear-selection" onClick={() => setPicked([])} className="ml-3 text-xs text-white/60 underline">Clear</button>
            </div>
            <button data-testid="trips-register-selected" onClick={() => navigate(`/trips/register?events=${picked.join(",")}`)}
              className="inline-flex items-center gap-2 bg-[#E63946] hover:bg-[#D90429] px-6 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm transition-colors">
              Register Interest for {picked.length > 1 ? "All" : "This Trip"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </PublicLayout>
  );
}

function TripCard({ ev, selected, onToggle }) {
  const closed = isClosed(ev);
  const divisions = ev.age_divisions || [];
  return (
    <Link to={`/interest/${ev.slug}`} data-testid={`trip-card-${ev.slug}`}
      className={`group relative block bg-white border-2 rounded-sm overflow-hidden hover:shadow-xl transition-all duration-300 ${selected ? "border-[#E63946]" : "border-slate-200 hover:border-[#0A192F]"}`}>
      <div className="relative h-48 bg-[#0A192F] overflow-hidden">
        {ev.hero_image ? (
          <img src={ev.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A192F] via-[#12294a] to-[#0A192F]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] to-transparent" />
        {ev.logo_image && (
          <img src={ev.logo_image} alt={`${ev.name} logo`} data-testid={`trip-logo-${ev.slug}`}
            className="absolute right-4 top-1/2 -translate-y-1/2 h-28 w-32 object-contain drop-shadow-xl group-hover:scale-105 transition-transform duration-500" />
        )}
        {!closed && (
          <button type="button" data-testid={`trip-select-${ev.slug}`} aria-pressed={selected}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(); }}
            className={`absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-full transition-colors ${selected ? "bg-[#E63946] text-white" : "bg-white/90 text-[#0A192F] hover:bg-white"}`}>
            {selected ? <><Check className="w-3 h-3" /> Added</> : <><Plus className="w-3 h-3" /> Add</>}
          </button>
        )}
        <div className={`absolute bottom-0 left-0 p-5 ${ev.logo_image ? "max-w-[62%]" : ""}`}>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-[#E63946]">{ev.event_type || "Showcase"}</div>
          <div className="font-display text-2xl uppercase tracking-tight text-white leading-tight">{ev.name}</div>
        </div>
        {closed && <span className="absolute top-3 right-3 text-[9px] font-bold uppercase bg-slate-700 text-white px-2 py-1 rounded-sm">Interest Closed</span>}
        {!closed && <div className="absolute top-3 right-3 z-10"><TripShareButton ev={ev} /></div>}
      </div>
      <div className="p-5">
        <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-slate-600">
          {ev.location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#E63946]" /> {ev.location}</span>}
          {(ev.start_date || ev.end_date) && <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-[#E63946]" /> {fmtDates(ev.start_date, ev.end_date)}</span>}
        </div>
        {divisions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {divisions.map((d) => <span key={d} className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-1 rounded-sm">{d}</span>)}
          </div>
        )}
        {ev.description && <p className="text-sm text-slate-500 mt-3 line-clamp-2 leading-relaxed">{ev.description}</p>}
        <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-wider text-[#E63946] group-hover:gap-3 transition-all">
          {closed ? "View Event" : "Register Your Interest"} <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    </Link>
  );
}
